import { useState, useMemo, type ElementType } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useCompanyFinancials, useFinancialsExcelExport } from "@/hooks/use-company-financials";
import {
    AlertCircle,
    Loader2,
    Table as TableIcon,
    Wallet,
    FileSpreadsheet,
    DollarSign,
    Download,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FinancialsTable } from "@/components/financials/FinancialsTable";
import {
    ClaimReceivablesSection,
    PaymentsSection,
    RevenueAccountsReceivableSection,
} from "@/components/financials/FinancialsMonthlyReport";

type ViewMode = "claims" | "receivables" | "payments" | "revenue-ar";

const TABS: { value: ViewMode; label: string; icon: ElementType }[] = [
    { value: "claims", label: "Claims", icon: TableIcon },
    { value: "receivables", label: "Receivables", icon: FileSpreadsheet },
    { value: "payments", label: "Payments", icon: Wallet },
    { value: "revenue-ar", label: "Revenue", icon: DollarSign },
];

export default function Financials() {
    const { data: report, isLoading, isError, error, refetch } = useCompanyFinancials();
    const { handleExport, isPending: isExporting } = useFinancialsExcelExport();
    const [viewMode, setViewMode] = useState<ViewMode>("claims");
    const [monthFilter, setMonthFilter] = useState<string | null>(null);

    const claims = useMemo(() => report?.claims ?? [], [report?.claims]);

    /** Called from monthly tabs when user clicks a month column */
    const handleMonthDrillDown = (month: string) => {
        setMonthFilter(month);
        setViewMode("claims");
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center py-24">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Layout>
        );
    }

    if (isError) {
        return (
            <Layout>
                <div className="md:px-4 md:py-8">
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Failed to load financials</AlertTitle>
                        <AlertDescription className="flex items-center gap-4">
                            <span>{error instanceof Error ? error.message : "An unexpected error occurred."}</span>
                            <Button variant="outline" size="sm" onClick={() => refetch()}>
                                Retry
                            </Button>
                        </AlertDescription>
                    </Alert>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 md:px-4 md:py-8">
                <div className="space-y-2">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="space-y-1">
                            <h1 className="text-2xl font-semibold text-foreground">Financials</h1>
                            <p className="text-muted-foreground md:hidden">Overview of financial data across all claims</p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExport}
                            disabled={isExporting}
                            className="h-8 self-start px-2 text-xs md:h-auto md:self-auto md:px-3"
                        >
                            {isExporting ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Download className="h-4 w-4 mr-2" />
                            )}
                            Export Excel
                        </Button>
                    </div>
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <p className="hidden text-muted-foreground md:block">Overview of financial data across all claims</p>
                        <div className="grid w-full grid-cols-2 gap-1 rounded-lg border bg-muted p-1 md:flex md:w-auto md:flex-wrap md:gap-0">
                            {TABS.map(({ value, label, icon: Icon }) => (
                                <Button
                                    key={value}
                                    variant={viewMode === value ? "secondary" : "ghost"}
                                    size="sm"
                                    onClick={() => setViewMode(value)}
                                    className={`w-full justify-center md:w-auto ${viewMode === value ? "bg-card shadow-sm hover:bg-card" : ""}`}
                                >
                                    <Icon className="h-4 w-4 mr-2" />
                                    {label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>

                {viewMode === "claims" && (
                    <div className="space-y-4">
                        {claims.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground border rounded-md bg-card">
                                No claims found. Create a claim to get started.
                            </div>
                        ) : (
                            <FinancialsTable
                                data={claims}
                                monthFilter={monthFilter}
                                onClearMonthFilter={() => setMonthFilter(null)}
                            />
                        )}
                    </div>
                )}
                {viewMode === "receivables" && <ClaimReceivablesSection data={report?.receivables ?? []} onMonthClick={handleMonthDrillDown} />}
                {viewMode === "payments" && <PaymentsSection data={report?.payments ?? []} onMonthClick={handleMonthDrillDown} />}
                {viewMode === "revenue-ar" && <RevenueAccountsReceivableSection data={report?.revenue ?? []} onMonthClick={handleMonthDrillDown} />}
            </div>
        </Layout>
    );
}
